import type { Limrun } from "@limrun/api";
import { PackageOpen, Search } from "lucide-react";
import { useMemo, useState } from "react";

import { transformUrl, useAuth } from "@/auth/auth";
import { useOrganization } from "@/client/backend/organization";
import { useAssets } from "@/client/director/assets";
import { DeviceInstallRelay } from "@/components/device-install/DeviceInstallRelay";
import { AppShell, EmptyState, PageHeader } from "@/components/layout/AppShell";
import { PlaystorePublishDialog } from "@/components/registry/PlaystorePublishDialog";
import { RunOnEmulatorButton } from "@/components/registry/RunOnEmulatorButton";
import { Badge } from "@/components/ui/badge";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";

const ASSET_LIST_LIMIT = 200;

type Asset = Limrun.AssetListResponse[number];

function isAppStoreAsset(asset: Asset): boolean {
	return asset.name.startsWith("appstore/");
}

function getAssetDisplayName(asset: Asset): string {
	return asset.displayName || (isAppStoreAsset(asset) ? asset.name.replace("appstore/", "") : asset.name);
}

function assetMatches(asset: Asset, search: string): boolean {
	if (!search) {
		return true;
	}
	return `${getAssetDisplayName(asset)} ${asset.name}`.toLowerCase().includes(search);
}

export function Registry() {
	const auth = useAuth();
	const [organization] = useOrganization();
	const [search, setSearch] = useState("");
	const organizationTid = organization !== "loading" ? organization.tid : undefined;
	const devRegistryApiUrl = import.meta.env.DEV
		? new URLSearchParams(window.location.search).get("registryApiUrl") || undefined
		: undefined;
	const configuredRegistryApiUrl = import.meta.env.VITE_REGISTRY_API_ENDPOINT as string | undefined;
	const registryApiUrl = devRegistryApiUrl ?? transformUrl(configuredRegistryApiUrl);
	const { data: userAssets, error: userAssetsError } = useAssets({
		organizationTid,
		limit: ASSET_LIST_LIMIT,
	});
	const { data: appStoreAssets, error: appStoreAssetsError } = useAssets({
		organizationTid,
		includeAppStore: true,
		limit: ASSET_LIST_LIMIT,
		namePrefixFilter: "appstore/",
	});
	const assets = useMemo<Asset[] | "loading">(() => {
		if (userAssets === "loading" || appStoreAssets === "loading") {
			return "loading";
		}
		return [
			...appStoreAssets.filter(isAppStoreAsset),
			...userAssets.filter((asset) => !isAppStoreAsset(asset)),
		];
	}, [appStoreAssets, userAssets]);
	const filteredAssets = useMemo(() => {
		if (assets === "loading") {
			return assets;
		}
		const normalizedSearch = search.trim().toLowerCase();
		return assets.filter((asset) => assetMatches(asset, normalizedSearch));
	}, [assets, search]);
	const loadError = userAssetsError || appStoreAssetsError;

	return (
		<AppShell title="Registry">

			<PageHeader title="Registry" description="Browse uploaded apps and install them on a device." />

			<div className="flex max-w-md items-center gap-3">
				<div className="relative flex-1">
					<Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
					<Input
						className="pl-9"
						aria-label="Search assets"
						placeholder="Search assets..."
						value={search}
						onChange={(event) => setSearch(event.currentTarget.value)}
					/>
				</div>
			</div>

			{loadError && (
				<div role="alert" className="rounded-lg border border-destructive/30 bg-destructive/10 p-4 text-sm text-destructive">
					Failed to load assets: {loadError.message}
				</div>
			)}

			{loadError ? null : filteredAssets === "loading" ? (
				<div className="py-12 text-center text-sm text-muted-foreground">Loading assets...</div>
			) : filteredAssets.length === 0 ? (
				<EmptyState icon={<PackageOpen />} title={search ? "No matching apps" : "No apps yet"} description={search ? "Try another name or clear the search." : "Upload a build artifact to install it on a device."} />
			) : (
				<div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
					{filteredAssets.map((asset) => (
						<AssetCard
							key={asset.id}
							asset={asset}
							registryApiUrl={registryApiUrl}
							registryToken={auth.token ?? undefined}
							organizationTid={organizationTid}
						/>
					))}
				</div>
			)}
		</AppShell>
	);
}

function AssetCard({
	asset,
	registryApiUrl,
	registryToken,
	organizationTid,
}: {
	asset: Asset;
	registryApiUrl?: string;
	registryToken?: string;
	organizationTid?: string;
}) {
	const appStore = isAppStoreAsset(asset);
	// Gradle-built assets carry no platform, so the filename suffix is the
	// only reliable format signal.
	const lowerName = asset.name.toLowerCase();
	const isApk = lowerName.endsWith(".apk");
	const isAab = lowerName.endsWith(".aab");
	return (
		<Card className="flex flex-col">
			<CardHeader>
				<div className="mb-3 flex h-12 w-12 items-center justify-center rounded-md bg-muted/50">
					<PackageOpen className="h-6 w-6 text-muted-foreground" />
				</div>
				<div className="flex items-start justify-between gap-3">
					<div className="min-w-0">
						<CardTitle className="truncate text-base">{getAssetDisplayName(asset)}</CardTitle>
						<CardDescription className="mt-1 truncate">{asset.name}</CardDescription>
					</div>
					{asset.os && <Badge variant="outline">{asset.os}</Badge>}
				</div>
			</CardHeader>
			<CardContent className="mt-auto space-y-4">
				<div className="flex flex-wrap gap-2">
					{appStore && <Badge variant="secondary">App Store</Badge>}
					{asset.md5 ? <Badge variant="outline">Uploaded</Badge> : <Badge variant="outline">Pending upload</Badge>}
				</div>
				{isApk ? (
					<RunOnEmulatorButton assetName={asset.name} disabled={!asset.md5} />
				) : isAab ? (
					<PlaystorePublishDialog
						assetName={asset.name}
						registryApiUrl={registryApiUrl}
						registryToken={registryToken}
						organizationId={organizationTid}
						disabled={!asset.md5}
					/>
				) : (
					<DeviceInstallRelay
						registryApiUrl={registryApiUrl}
						registryToken={registryToken}
						organizationId={organizationTid}
						installSource={{ assetName: asset.name }}
					/>
				)}
			</CardContent>
		</Card>
	);
}
